/**
 * Terrain — the map as flat, serializable data.
 *
 * Tiles are stored as a row-major array of small integers indexed into
 * `TILE_KINDS`, not as objects. Three reasons, all of which bite later if we
 * get it wrong now:
 *
 * - **Cloning.** `advance` deep-copies world state every tick (`cloneWorld`).
 *   A 128×128 map of numbers copies as one `slice`; the same map as objects
 *   copies as 16,384 allocations, ten times a second, forever.
 * - **The wire.** M0-04 streams tiles to clients as the player walks. Integers
 *   are the compact form, and the client needs a lookup table anyway to turn a
 *   tile into a glyph.
 * - **Hashing.** Fewer, smaller values in the canonical JSON that the
 *   determinism test fingerprints.
 *
 * What a tile *means* — passable, opaque, what `examine` reads out — lives in
 * `TILE_DEFS` here rather than in the map, because it is the same for every
 * instance of that tile and belongs to the rules, not to the world. When
 * M3-01 replaces the stub map with real worldgen, only the producer changes;
 * everything reading a map reads it through these helpers.
 */
/**
 * Every terrain the MVP knows. `void` is the out-of-bounds sentinel — reading
 * off the edge of the map returns it rather than throwing, so callers walking
 * a viewport never have to bounds-check twice.
 *
 * A tile's numeric id is its index here, so **entries may be appended but
 * never reordered or removed**: the ids are in saved worlds (M0-07) and on the
 * wire (M0-03).
 */
export const TILE_KINDS = [
    'void',
    'grass',
    'dirt',
    'rock',
    'water',
    'reeds',
    'tree',
    'wall',
    // M3-01 — the biomes of Cwm Twrch, in the order the height×moisture table
    // walks them: salt water up to bare rock.
    'sea',
    'shore',
    'saltmarsh',
    'meadow',
    'ffridd',
    'oakwood',
    'hazelwood',
    'bog',
    'moor',
    'crag',
    'ford',
    // M3-02 — the hand-shaped places. Sites are terrain rather than entities
    // because terrain is what the snapshot already streams and what `examine`
    // already reads; a cauldron that needed an entity table would arrive after
    // the entity table, and M4-07 wants it standing on the moor now.
    'path',
    'floor',
    'door',
    'hearth',
    'well',
    'menhir',
    'cauldron',
    'mound',
    'wallow',
    'fence',
    // M4-08 — the Meddyg's cot. One tile in the whole valley, and the scarcity is
    // the design: the second patient gets the hall floor.
    'cot',
    // M5-01 — turned ground. The Meddyg's physic garden, and the reason the pen
    // is now a pen: the two were the same fenced square until the village got
    // livestock in it, and a healer growing her garlic among the sheep was a
    // level-design joke nobody meant to make.
    'garden',
    // The old pagan holy ground south of Llan-y-Nant. Appended rather than placed
    // beside the other M3-02 site tiles so every numeric tile id already written
    // to a save keeps meaning exactly what it meant before the relic was added.
    'sun-stone',
];
/**
 * What a tile is *made of*, as distinct from what it is.
 *
 * Examine reads it out (M0-06), but that is the smallest of its jobs: material
 * is the hook everything physical later hangs on — whether a blade bites or
 * skids, whether a fire catches, whether tracks take an impression (M4-02).
 * Naming it now costs one field and means later systems ask the terrain rather
 * than carrying their own table of tile kinds that has to be kept in step.
 */
export const MATERIALS = [
    'nothing',
    'earth',
    'stone',
    'water',
    'wood',
    'plant',
    // M3-01. `sand` and `peat` are here for M3-08 before they are here for
    // anything else: what takes a print and holds it is a property of the
    // ground, and the tracking rules should ask the material rather than carry
    // their own list of which biomes are soft.
    'sand',
    'peat',
    'metal',
];
/** What a tile is, before the site templates carve anything into it. */
const DEFAULTS = { moveCost: 1, footing: 1 };
/**
 * The rules for each terrain. Deliberately thin: the MVP needs collision and
 * a description, and inventing movement-cost multipliers or swim rules before
 * there is a game to tune them against is how content becomes fiction.
 */
export const TILE_DEFS = {
    void: {
        ...DEFAULTS,
        kind: 'void',
        passable: false,
        opaque: true,
        label: 'the edge of the world',
        material: 'nothing',
        detail: 'There is nothing there to look at, and no way to go.',
    },
    grass: {
        ...DEFAULTS,
        kind: 'grass',
        passable: true,
        opaque: false,
        label: 'rough pasture',
        material: 'plant',
        detail: 'Thin turf over sour ground, cropped short by something.',
    },
    dirt: {
        ...DEFAULTS,
        kind: 'dirt',
        passable: true,
        opaque: false,
        label: 'bare earth',
        material: 'earth',
        detail: 'Trodden soil, dark and soft enough to hold a print.',
    },
    rock: {
        ...DEFAULTS,
        kind: 'rock',
        passable: true,
        opaque: false,
        label: 'broken scree',
        material: 'stone',
        detail: 'Loose slate that shifts underfoot and gives nothing away.',
        moveCost: 1.3,
        footing: 0.85,
    },
    water: {
        ...DEFAULTS,
        kind: 'water',
        passable: false,
        opaque: false,
        label: 'cold running water',
        material: 'water',
        detail: 'Peat-brown and quick. Deep enough to be a decision.',
    },
    reeds: {
        ...DEFAULTS,
        kind: 'reeds',
        passable: true,
        opaque: true,
        label: 'a stand of reeds',
        material: 'plant',
        detail: 'Tall enough to stand in and not be seen standing there.',
        moveCost: 1.4,
    },
    tree: {
        ...DEFAULTS,
        kind: 'tree',
        passable: false,
        opaque: true,
        label: 'a stunted oak',
        material: 'wood',
        detail: 'Wind-bent and grey with lichen, too thick to push past.',
    },
    wall: {
        ...DEFAULTS,
        kind: 'wall',
        passable: false,
        opaque: true,
        label: 'a drystone wall',
        material: 'stone',
        detail: 'Slate laid without mortar, waist-high and older than anyone here.',
    },
    /* ---------------------------------------------------------------------- *
     * M3-01 — the biomes
     * ---------------------------------------------------------------------- */
    sea: {
        ...DEFAULTS,
        kind: 'sea',
        passable: false,
        opaque: false,
        label: 'the open sea',
        material: 'water',
        detail: 'Grey water heaving in from the west, with no bottom worth finding.',
    },
    shore: {
        ...DEFAULTS,
        kind: 'shore',
        passable: true,
        opaque: false,
        label: 'a shingle strand',
        material: 'sand',
        detail: 'Wet stones and coarse sand, hissing as each wave draws back off it.',
        moveCost: 1.15,
        footing: 0.9,
    },
    saltmarsh: {
        ...DEFAULTS,
        kind: 'saltmarsh',
        passable: true,
        opaque: false,
        label: 'salt marsh',
        material: 'plant',
        detail: 'Sea-grass cut through with brackish channels. The ground gives without warning.',
        moveCost: 1.6,
        footing: 0.75,
    },
    meadow: {
        ...DEFAULTS,
        kind: 'meadow',
        passable: true,
        opaque: false,
        label: 'a wet meadow',
        material: 'plant',
        detail: 'Rhos pasture — moor-grass and rushes, grazed down to the quick in patches.',
    },
    ffridd: {
        ...DEFAULTS,
        kind: 'ffridd',
        passable: true,
        opaque: false,
        label: 'the ffridd',
        material: 'plant',
        detail: 'Bracken and thorn on the slope where the fields give up and the hill begins.',
        moveCost: 1.3,
        footing: 0.9,
    },
    oakwood: {
        ...DEFAULTS,
        kind: 'oakwood',
        passable: true,
        opaque: false,
        label: 'oakwood floor',
        material: 'plant',
        detail: 'Leaf mould and moss beneath sessile oaks. What light gets down here is green.',
        moveCost: 1.2,
    },
    hazelwood: {
        ...DEFAULTS,
        kind: 'hazelwood',
        passable: true,
        opaque: false,
        label: 'hazel coppice',
        material: 'plant',
        detail: 'Cut stools throwing up straight rods, close enough together to lose a man in.',
        moveCost: 1.25,
    },
    bog: {
        ...DEFAULTS,
        kind: 'bog',
        passable: true,
        opaque: false,
        label: 'blanket bog',
        material: 'peat',
        detail: 'Sphagnum floating on black peat water. Every step is taken back out of you.',
        moveCost: 2,
        footing: 0.6,
    },
    moor: {
        ...DEFAULTS,
        kind: 'moor',
        passable: true,
        opaque: false,
        label: 'open moor',
        material: 'plant',
        detail: 'Heather and cotton-grass to the skyline, and a wind with nothing to stop it.',
        moveCost: 1.15,
    },
    crag: {
        ...DEFAULTS,
        kind: 'crag',
        passable: false,
        opaque: true,
        label: 'a crag',
        material: 'stone',
        detail: 'Bare rock broken out through the hill, too sheer to be climbed by anyone in a hurry.',
    },
    ford: {
        ...DEFAULTS,
        kind: 'ford',
        passable: true,
        opaque: false,
        label: 'a ford',
        material: 'water',
        detail: 'The river runs wide and shallow over gravel here. Shin-deep, and very cold.',
        moveCost: 1.8,
        footing: 0.7,
    },
    /* ---------------------------------------------------------------------- *
     * M3-02 — the sites
     * ---------------------------------------------------------------------- */
    path: {
        ...DEFAULTS,
        kind: 'path',
        passable: true,
        opaque: false,
        label: 'a worn path',
        material: 'earth',
        detail: 'Trodden bare by feet and hooves all going the same way for longer than anyone recalls.',
        moveCost: 0.8,
    },
    floor: {
        ...DEFAULTS,
        kind: 'floor',
        passable: true,
        opaque: false,
        label: 'a beaten floor',
        material: 'earth',
        detail: 'Earth trodden hard and swept, with fresh rushes strewn across it.',
    },
    door: {
        ...DEFAULTS,
        kind: 'door',
        passable: true,
        opaque: false,
        label: 'a doorway',
        material: 'wood',
        detail: 'A low doorway under an oak lintel. You would have to stoop to go through.',
    },
    /**
     * A sick-bed (M4-08). The Meddyg's cot, and in the MVP the only one.
     *
     * A tile of its own rather than a flag on `floor`, for the reason `hearth` is
     * a tile: it is a *place* with a rule attached, the rule is read by asking
     * the ground what it is, and there is exactly one of them in the valley —
     * which is the scarcity the ticket wants. The second patient gets the floor.
     */
    cot: {
        ...DEFAULTS,
        kind: 'cot',
        passable: true,
        opaque: false,
        label: 'a sick-bed',
        material: 'plant',
        detail: 'A low cot of ash and rope with a straw tick on it, close enough to the fire to be ' +
            'warm and far enough not to catch. It smells of herbs and of people who did not all live.',
    },
    hearth: {
        ...DEFAULTS,
        kind: 'hearth',
        passable: false,
        opaque: false,
        label: 'a hearth',
        material: 'stone',
        detail: 'A ring of stones with the ash banked to one side, and the smell of old smoke in it.',
    },
    well: {
        ...DEFAULTS,
        kind: 'well',
        passable: false,
        opaque: false,
        label: 'the well',
        material: 'stone',
        detail: 'Slabbed round and dark. The water down there is clean, and colder than the river.',
    },
    menhir: {
        ...DEFAULTS,
        kind: 'menhir',
        passable: false,
        opaque: true,
        label: 'a standing stone',
        material: 'stone',
        detail: 'A grey pillar-stone, lichened to shoulder height, set here by nobody living.',
    },
    cauldron: {
        ...DEFAULTS,
        kind: 'cauldron',
        passable: false,
        opaque: false,
        label: 'the Pair Dadeni',
        material: 'metal',
        detail: 'A black cauldron on three legs, wide enough to seat a man. It is empty, and it is not.',
    },
    mound: {
        ...DEFAULTS,
        kind: 'mound',
        passable: true,
        opaque: false,
        label: 'a burial mound',
        material: 'earth',
        detail: "A green swelling in the ground, too regular to be the hill's own work.",
        moveCost: 1.3,
    },
    wallow: {
        ...DEFAULTS,
        kind: 'wallow',
        passable: true,
        opaque: false,
        label: 'a wallow',
        material: 'earth',
        detail: 'Mud churned to soup and stinking, the tree-bases beside it rubbed smooth and bare.',
        moveCost: 1.7,
        footing: 0.6,
    },
    garden: {
        ...DEFAULTS,
        kind: 'garden',
        passable: true,
        opaque: false,
        label: 'a physic garden',
        material: 'earth',
        detail: 'Beds dug in narrow rigs and weeded to the inch — garlic, and something feathery, and a ' +
            'row of stakes with string between them. Somebody minds this.',
        moveCost: 1.2,
    },
    fence: {
        ...DEFAULTS,
        kind: 'fence',
        passable: false,
        opaque: false,
        label: 'a hurdle fence',
        material: 'wood',
        detail: 'Hazel rods woven between driven stakes, high enough to keep a ewe where she is put.',
    },
    'sun-stone': {
        ...DEFAULTS,
        kind: 'sun-stone',
        passable: true,
        opaque: false,
        label: 'the Sun Stone',
        material: 'stone',
        detail: 'A many-rayed wheel is cut deep into a black flagstone. Rain lies everywhere but in ' +
            'the grooves, and the grooves are warm.',
    },
};
/** Numeric id of each kind — the value the map actually stores. */
export const TILE_IDS = Object.fromEntries(TILE_KINDS.map((kind, id) => [kind, id]));
/** The out-of-bounds tile, resolved once. */
const VOID_ID = TILE_IDS.void;
/** A map of one terrain, ready to be carved into. */
export function createTileMap(width, height, fill = 'grass') {
    if (!Number.isInteger(width) || !Number.isInteger(height) || width < 0 || height < 0) {
        throw new RangeError(`createTileMap needs non-negative integer bounds (got ${width}×${height})`);
    }
    return { width, height, tiles: new Array(width * height).fill(TILE_IDS[fill]) };
}
export function inBounds(map, x, y) {
    return x >= 0 && y >= 0 && x < map.width && y < map.height;
}
/** The stored id at a coordinate, or `void`'s id off the edge. */
export function tileIdAt(map, x, y) {
    if (!inBounds(map, x, y))
        return VOID_ID;
    return map.tiles[y * map.width + x] ?? VOID_ID;
}
export function tileAt(map, x, y) {
    return TILE_KINDS[tileIdAt(map, x, y)] ?? 'void';
}
export function tileDefAt(map, x, y) {
    return TILE_DEFS[tileAt(map, x, y)];
}
/** Writes a tile. Out-of-bounds writes are ignored rather than thrown. */
export function setTile(map, x, y, kind) {
    if (!inBounds(map, x, y))
        return;
    map.tiles[y * map.width + x] = TILE_IDS[kind];
}
/**
 * Terrain passability only — it says nothing about who is standing there.
 * Occupancy is an actor question, and lives with the movement rules.
 */
export function isPassable(map, x, y) {
    return tileDefAt(map, x, y).passable;
}
export function isOpaque(map, x, y) {
    return tileDefAt(map, x, y).opaque;
}
/**
 * The tile kinds that are *under a roof* (RC1-07).
 *
 * Asked of the kind rather than a per-tile flag or a flood-fill, and it can be:
 * a beaten floor, a doorway under its lintel and a sick-bed only ever exist
 * inside an authored building (see the site templates), so standing on one is
 * standing under cover by construction. `hearth` is deliberately not here — the
 * village hearth already earns `bed` rest on its own, and a fire is not a roof.
 */
export const SHELTERED_TILES = ['floor', 'door', 'cot'];
/**
 * Is this tile under a roof? The one question the rain-sensitive rules —
 * `restQualityAt`, `buildChance` — ask to know whether the weather reaches here.
 */
export function isSheltered(map, x, y) {
    return SHELTERED_TILES.includes(tileAt(map, x, y));
}
/**
 * The step-cooldown multiplier for the ground at a coordinate (M3-01).
 *
 * Off the map this is `void`'s 1 rather than a throw, for the reason `tileAt`
 * returns `void`: callers walking a viewport should not have to bounds-check
 * twice, and nothing can stand on `void` anyway.
 */
export function moveCostAt(map, x, y) {
    return tileDefAt(map, x, y).moveCost;
}
/** The defence-dice multiplier for the ground at a coordinate (M3-01). */
export function footingAt(map, x, y) {
    return tileDefAt(map, x, y).footing;
}
//# sourceMappingURL=map.js.map